import { useEffect, useState, lazy, Suspense } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { applyLandingLanguage, restoreAppLanguage } from "./i18n";
import { useAuthStore } from "./store/authStore";
import { isRunningStandalone } from "./services/pwaInstall";
import { isLandingPath as matchLandingPath, landingLang } from "./utils/landing";
import { LandingPage } from "./pages/LandingPage";
import { Loader2 } from "lucide-react";

// Duração do splash de abertura (estilo app nativo) mostrado antes do login no PWA.
const PWA_SPLASH_MS = 700;

// Splash de abertura com a logo Taskletto sobre fundo ink, usado enquanto a
// sessão é restaurada e (no PWA) antes de abrir o login, simulando o
// carregamento de um app nativo.
function BrandSplash() {
  return (
    <div className="min-h-screen bg-ink flex flex-col justify-center items-center font-sans">
      <img
        src="/logo-taskletto-light.png"
        alt="Taskletto"
        className="h-10 w-auto animate-pulse"
      />
    </div>
  );
}

// A landing page é o primeiro paint para visitantes deslogados, então fica no
// bundle inicial (eager). Todas as demais telas — especialmente o Dashboard e o
// visualizador de notas, que puxam o editor pesado (Tiptap, highlight.js,
// lowlight, markdown-it, react-easy-crop) — são carregadas sob demanda para não
// bloquear a renderização do LCP da landing.
const AuthPage = lazy(() =>
  import("./pages/AuthPage").then((m) => ({ default: m.AuthPage }))
);
const AuthCallbackPage = lazy(() =>
  import("./pages/AuthCallbackPage").then((m) => ({ default: m.AuthCallbackPage }))
);
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage }))
);
const AdminPage = lazy(() =>
  import("./pages/AdminPage").then((m) => ({ default: m.AdminPage }))
);
const PublicNotePage = lazy(() =>
  import("./pages/PublicNotePage").then((m) => ({ default: m.PublicNotePage }))
);
const TermsPage = lazy(() =>
  import("./pages/TermsPage").then((m) => ({ default: m.TermsPage }))
);
const PrivacyPage = lazy(() =>
  import("./pages/PrivacyPage").then((m) => ({ default: m.PrivacyPage }))
);
const AboutPage = lazy(() =>
  import("./pages/AboutPage").then((m) => ({ default: m.AboutPage }))
);
const DrawingViewerPage = lazy(() =>
  import("./pages/DrawingViewerPage").then((m) => ({ default: m.DrawingViewerPage }))
);

// Splash reutilizável enquanto um chunk de rota carrega.
function RouteFallback() {
  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col justify-center items-center font-sans">
      <Loader2 className="animate-spin text-primary-600" size={32} />
    </div>
  );
}

// Raiz: "/" é sempre a landing na web (mesmo logado); demais caminhos sob
// "/*" abrem o Dashboard quando autenticado. Único caminho splat para que o
// Dashboard NÃO remonte ao alternar entre subrotas — assim o estado (notas,
// cadernos) persiste.
function RootRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // A landing vive em "/" (pt-BR) e "/en" (inglês) — a URL é a fonte de verdade
  // do idioma, para SEO (páginas estáticas distintas + hreflang).
  const isLandingPath = matchLandingPath(location.pathname);

  // Na landing o idioma segue a URL (visita direta a /en por crawler ou link
  // compartilhado já abre em inglês) — exibição apenas, sem virar preferência.
  // Ao sair dela, o app volta ao idioma escolhido pelo usuário.
  useEffect(() => {
    if (isLandingPath) {
      applyLandingLanguage(landingLang(location.pathname));
    } else {
      restoreAppLanguage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (isLandingPath) {
    // No app instalado (PWA standalone) não existe landing de marketing: a
    // tela inicial é sempre o app (dashboard, se logado) ou o login.
    if (isRunningStandalone()) {
      return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
    }
    // Na web, "/" continua sendo a landing mesmo logado — o dashboard vive em
    // /dashboard — para manter a página de marketing sempre alcançável.
    return (
      <LandingPage
        isAuthenticated={isAuthenticated}
        onLoginClick={() => navigate(isAuthenticated ? "/dashboard" : "/login")}
        onNavigate={(path) => navigate(path)}
      />
    );
  }

  if (isAuthenticated) return <DashboardPage />;

  // Rota protegida (ex.: /note/:id vindo de um email de compartilhamento) acessada
  // sem login: guarda o destino para retomar após login/cadastro+verificação.
  const target = location.pathname + location.search;
  if (target !== "/login") {
    localStorage.setItem("pendingRedirect", target);
  }
  return <Navigate to="/login" replace />;
}

// Página de login/cadastro/recuperação. Redireciona se já logado.
function LoginRoute() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  // No PWA standalone não existe landing para "voltar" — a tela inicial
  // já é sempre o login — então o botão de voltar não é exibido.
  return (
    <AuthPage onBack={isRunningStandalone() ? undefined : () => navigate("/")} />
  );
}

function AuthCallbackRoute() {
  const navigate = useNavigate();
  return <AuthCallbackPage onGoToLogin={() => navigate("/login")} />;
}

// Painel administrativo em página dedicada. Exige login + papel ADMIN.
function AdminRoute() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== "ADMIN") return <Navigate to="/dashboard" replace />;
  return <AdminPage onExit={() => navigate("/dashboard")} />;
}

function SharedNoteRoute() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return (
    <PublicNotePage
      shareToken={token ?? ""}
      onGoBack={() => navigate(isAuthenticated ? "/dashboard" : "/")}
      isAuthenticated={isAuthenticated}
      onSignUp={() => navigate("/login")}
    />
  );
}

// Quadro público via link (somente leitura, sem necessidade de login).
function PublicDrawingRoute() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return <DrawingViewerPage mode="public" identifier={token ?? ""} onGoBack={() => navigate(isAuthenticated ? "/dashboard" : "/")} />;
}

// Quadro compartilhado por email (somente leitura). Exige login; guarda o
// destino para retomar após autenticar, como as notas compartilhadas.
function SharedDrawingRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  if (!isAuthenticated) {
    localStorage.setItem("pendingRedirect", location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  return <DrawingViewerPage mode="authed" identifier={id ?? ""} onGoBack={() => navigate("/dashboard")} />;
}

function App() {
  const { isAuthenticated, initializing, initialize } = useAuthStore();
  const navigate = useNavigate();

  // No PWA (start_url="/login" no manifest), o app já nasce em /login sem
  // nunca passar por "/" — por isso o splash de abertura é controlado aqui,
  // no nível do App, e não em uma rota específica: assim ele sempre aparece
  // no boot em modo standalone, independente da rota inicial.
  const [pwaSplashDone, setPwaSplashDone] = useState(() => !isRunningStandalone());

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (pwaSplashDone) return;
    const timer = setTimeout(() => setPwaSplashDone(true), PWA_SPLASH_MS);
    return () => clearTimeout(timer);
  }, [pwaSplashDone]);

  // Retoma o destino guardado (ex.: link de nota compartilhada) após autenticar.
  // Cobre login e cadastro+verificação, pois ambos terminam com isAuthenticated=true.
  useEffect(() => {
    if (!isAuthenticated) return;
    const target = localStorage.getItem("pendingRedirect");
    if (target) {
      localStorage.removeItem("pendingRedirect");
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Splash de abertura: enquanto a sessão é restaurada, ou (no PWA) durante
  // o tempo mínimo do splash de boot, simulando a abertura de um app nativo.
  if ((initializing && !isAuthenticated) || !pwaSplashDone) {
    return <BrandSplash />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/auth/callback" element={<AuthCallbackRoute />} />
        <Route path="/shared/:token" element={<SharedNoteRoute />} />
        <Route path="/d/:token" element={<PublicDrawingRoute />} />
        <Route path="/quadro/:id" element={<SharedDrawingRoute />} />
        <Route path="/terms" element={<TermsPage onGoBack={() => navigate("/")} />} />
        <Route path="/privacy" element={<PrivacyPage onGoBack={() => navigate("/")} />} />
        <Route path="/sobre" element={<AboutPage onGoBack={() => navigate("/")} />} />
        <Route path="/admin" element={<AdminRoute />} />
        {/* "/" e "/note/:noteId" caem aqui sem remontar o Dashboard */}
        <Route path="/*" element={<RootRoute />} />
      </Routes>
    </Suspense>
  );
}

export default App;
